'use client';

import { Modal } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';

interface ButtonModalProps {
  text?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  variant: 'primary' | 'secondary' | 'home' | 'link';
  children?: React.ReactNode;
  ariaLabel?: string;
}

export function ButtonModal(props: ButtonModalProps) {
  const { text, size, variant, children, ariaLabel } = props;
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    // Return focus to the button that opened the modal
    buttonRef.current?.focus();
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const buttonProps = {
    ref: buttonRef,
    onClick: openModal,
    'aria-haspopup': 'dialog' as const,
    'aria-expanded': isOpen,
    'aria-label': ariaLabel || text,
  };

  return (
    <>
      {variant === 'primary' && (
        <button
          {...buttonProps}
          className="radius mt-4 flex min-h-10 items-center justify-center rounded-md border-2 border-fp-green bg-fp-green px-8 text-center text-white hover:border-2 hover:border-fp-green hover:bg-white hover:text-fp-green"
        >
          {text}
        </button>
      )}

      {variant === 'secondary' && (
        <button
          {...buttonProps}
          className="mt-4 rounded-lg border-[1px] border-fp-dark-gray bg-white px-6 py-3 text-sm font-medium hover:border-fp-green hover:bg-fp-green hover:text-white"
        >
          {text}
        </button>
      )}

      {variant === 'home' && (
        <button
          {...buttonProps}
          className="rounded-md border-2 bg-white px-6 py-2 text-sm text-fp-dark-gray hover:border-fp-green hover:bg-fp-green hover:text-white"
        >
          {text}
        </button>
      )}

      {variant === 'link' && (
        <button
          {...buttonProps}
          className="text-fp-green underline"
        >
          {text}
        </button>
      )}

      <Modal
        ref={modalRef}
        aria-modal="true"
        role="dialog"
        aria-labelledby="modal-title"
        show={isOpen}
        size={size}
        dismissible
        onClose={closeModal}
      >
        <Modal.Header className="!absolute right-0 z-10 border-none" id="modal-title" />
        <Modal.Body>{children}</Modal.Body>
      </Modal>
    </>
  );
}
